
/**
 * Format an integer to a string with thousand-separators.
 *
 * @param {string} value Integer-as-string value
 *
 * @return string
 */
export function formatInteger (value) {
    if (value === "null" || value === null) {
        return "<span class='null'>NULL</span>"
    }

    return `<span>${parseInt(value).toLocaleString()}</span>`
}

/**
 * Format a date value to mm/dd/yyyy format in HTML.
 *
 * @param {string} value Date-as-string value
 *
 * @return string
 */
export function formatDate (value) {
    let date, output, parts

    if (value === "null" || value === null) {
        return "<span class='null'>NULL</span>"
    }

    if (value instanceof Date) {
        output = `${value.getMonth() + 1}/${value.getDate()}/${value.getFullYear()}`

        return `<span>${output}</span>`
    }

    try {
        parts = value.split("-")

        // month is 0-index, so prepare
        parts[1] = parts[1] - 1

        date = new Date(...parts)
    } catch (error) {
        return `<span title='Invalid Date'>${value}</span>`
    }

    if (date.toString() === "Invalid Date") {
        return `<span title='Invalid Date'>${value}</span>`
    }

    output = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`

    return `<span>${output}</span>`
}

/**
 * Format a date value to mm/dd/yyyy format.
 *
 * @param {string} value Integer-as-string value
 *
 * @return {string|null}
 */
export function formatDateRaw (value) {
    let date, parts

    if (value === "null" || value === null) {
        return null
    }

    if (value instanceof Date) {
        return `${value.getMonth() + 1}/${value.getDate()}/${value.getFullYear()}`
    }

    try {
        parts = value.split("-")

        // month is 0-index, so prepare
        parts[1] = parts[1] - 1

        date = new Date(...parts)
    } catch (error) {
        return null
    }

    if (date.toString() === "Invalid Date") {
        return null
    }

    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`
}

/**
 * Format a phone number to a human-readable string.
 *
 * @param {string} value Phone-number-as-string value
 *
 * @return string
 */
export function formatPhone (value) {
    let output

    if (value === "null" || value === null) {
        return "<span class='null'>NULL</span>"
    }

    // US regular
    if (value.length === 10) {
        output = `(${value.substring(0, 3)}) ${value.substring(3, 6)}-${value.substring(6)}`
    }

    // US with international prefix
    if (value.length === 11) {
        output = `${value[0]}-${value.substring(1, 4)}-${value.substring(4, 7)}-${value.substring(7)}`
    }

    return `<span>${output}</span>`
}

/**
 * Format a string by either plainly displaying the value, or displaying null descriptions.
 *
 * @param {string} value Value string
 */
export function formatString (value) {
    if (value === "null" || value === null) {
        return "<span class='null'>NULL</span>"
    }

    return `<span>${value}</span>`
}

/**
 * Format a software type into a more human-readable format.
 *
 * @param {string} value Software type
 */
export function formatSoftware (value) {
    let output

    if (value === "null" || value === null) {
        return "<span class='null'>NULL</span>"
    }

    switch (value.trim()) {
    case "syrasoft":
        output = "Syrasoft"
        break
    case "storedge":
        output = "Storedge"
        break
    case "easystorage":
        output = "EasyStorage"
        break
    case "sitelink_web":
        output = "SiteLink - Web"
        break
    default:
        output = value.trim()
        break
    }

    return `<span class="software-badge ${value.trim()}">${output}</span>`
}

export const letterBatchesColumns = [
    {
        name: "facility_number",
        title: "Facility Number",
        sortField: "facility_number"
    },
    {
        name: "letter_type",
        title: "Letter Type",
        dataClass: "center aligned",
        titleClass: "center aligned",
        sortField: "letter_type"
    },
    {
        name: "tenant_count",
        title: "Tenant Count",
        dataClass: "right aligned",
        titleClass: "right aligned",
        sortField: "tenant_count"
    },
    {
        name: "__slot:save_time",
        title: "Date Generated",
        dataClass: "center aligned",
        titleClass: "center aligned",
        sortField: "save_time"
    },
    {
        name: "__slot:mail_time",
        title: "Date Mailed",
        dataClass: "center aligned",
        titleClass: "center aligned",
        sortField: "mail_time"
    },
    {
        name: "__slot:download",
        title: "Download",
        dataClass: "center aligned",
        titleClass: "center aligned"
    }
]

export const listFacilitiesColumns = [
    {
        name: "__slot:actions",
        title: "Actions",
        dataClass: "center aligned",
        titleClass: "center aligned",
        width: "135px"
    },
    {
        name: "__slot:facility_number",
        title: "Facility Number",
        width: "250px"
    },
    {
        name: "account_name",
        title: "Account Name",
        callback: formatString,
        width: "250px"
    },
    {
        name: "corporate_group",
        title: "Corporate Group",
        callback: formatString,
        width: "250px"
    },
    {
        name: "__slot:city",
        title: "City",
        width: "250px"
    },
    {
        name: "tenant_count",
        title: "Tenants",
        dataClass: "center aligned",
        titleClass: "center aligned",
        callback: formatInteger,
        width: "250px"
    },
    {
        name: "software",
        title: "Software",
        dataClass: "center aligned",
        titleClass: "center aligned",
        callback: formatSoftware,
        width: "250px"
    }
]

export const sendLettersColumns = [
    {
        name: "__slot:checkbox",
        dataClass: "center aligned no-click",
        width: "50px"
    },
    {
        name: "__slot:name",
        title: "Name",
        width: "200px"
    },
    {
        name: "__slot:cycle_label",
        title: "Cycle",
        dataClass: "center aligned",
        titleClass: "center aligned",
        width: "150px"
    },
    {
        name: "__slot:initial_letter_sent",
        title: "Audit Letter Sent",
        dataClass: "center aligned",
        titleClass: "center aligned",
        width: "200px"
    },
    {
        name: "__slot:reminder_letter_sent",
        title: "Reminder Letter Sent",
        dataClass: "center aligned",
        titleClass: "center aligned",
        width: "200px"
    },
    {
        name: "__slot:welcome_letter_sent",
        title: "Welcome Letter Sent",
        dataClass: "center aligned",
        titleClass: "center aligned",
        width: "200px"
    }
]

export const masterAuditColumns = [
    {
        name: "unit",
        sortField: "unit",
        title: "Unit",
        dataClass: "center aligned",
        titleClass: "center aligned",
        width: "125px"
    },
    {
        name: "__slot:name",
        sortField: "last_name",
        title: "Name",
        width: "250px"
    },
    {
        name: "__slot:cycle",
        title: "Cycle",
        dataClass: "center aligned",
        titleClass: "center aligned",
        width: "150px"
    },
    {
        name: "initial_letter_sent_date",
        sortField: "initial_letter_sent_date",
        title: "Audit Letter Sent",
        dataClass: "center aligned",
        titleClass: "center aligned",
        callback: formatDate,
        width: "200px"
    },
    {
        name: "reminder_letter_sent_date",
        sortField: "reminder_letter_sent_date",
        title: "Reminder Letter Sent",
        dataClass: "center aligned",
        titleClass: "center aligned",
        callback: formatDate,
        width: "200px"
    },
    {
        name: "__slot:status",
        title: "Status",
        width: "225px"
    },
    {
        name: "__slot:charge_apply_date",
        sortField: "charge_apply_date",
        title: "Setup Date",
        dataClass: "center aligned",
        titleClass: "center aligned",
        width: "200px"
    },
    {
        name: "insurance_effective_date",
        sortField: "insurance_effective_date",
        title: "Insurance Effective Date",
        dataClass: "center aligned",
        titleClass: "center aligned",
        callback: formatDate,
        width: "250px"
    },
    {
        name: "__slot:billing_date",
        sortField: "billing_date",
        title: "Billing Date",
        dataClass: "center aligned",
        titleClass: "center aligned",
        width: "175px"
    },
    {
        name: "__slot:charge_added",
        sortField: "charge_applied_date",
        title: "Charge Added",
        dataClass: "center aligned",
        titleClass: "center aligned",
        width: "200px"
    },
    {
        name: "welcome_letter_sent_date",
        sortField: "welcome_letter_sent_date",
        title: "Welcome Letter Sent",
        dataClass: "center aligned",
        titleClass: "center aligned",
        callback: formatDate,
        width: "200px"
    }
]

export const cycleAuditColumns = [
    {
        name: "unit",
        sortField: "unit",
        title: "Unit",
        dataClass: "center aligned",
        titleClass: "center aligned",
        width: "125px"
    },
    {
        name: "__slot:name",
        sortField: "last_name",
        title: "Name",
        width: "250px"
    },
    {
        name: "initial_letter_sent_date",
        sortField: "initial_letter_sent_date",
        title: "Audit Letter Sent",
        dataClass: "center aligned",
        titleClass: "center aligned",
        callback: formatDate,
        width: "200px"
    },
    {
        name: "reminder_letter_sent_date",
        sortField: "reminder_letter_sent_date",
        title: "Reminder Letter Sent",
        dataClass: "center aligned",
        titleClass: "center aligned",
        callback: formatDate,
        width: "200px"
    },
    {
        name: "__slot:status",
        title: "Status",
        width: "225px"
    },
    {
        name: "__slot:charge_apply_date",
        sortField: "charge_apply_date",
        title: "Setup Date",
        dataClass: "center aligned",
        titleClass: "center aligned",
        width: "200px"
    },
    {
        name: "insurance_effective_date",
        sortField: "insurance_effective_date",
        title: "Insurance Effective Date",
        dataClass: "center aligned",
        titleClass: "center aligned",
        callback: formatDate,
        width: "250px"
    },
    {
        name: "__slot:billing_date",
        sortField: "billing_date",
        title: "Billing Date",
        dataClass: "center aligned",
        titleClass: "center aligned",
        width: "175px"
    },
    {
        name: "__slot:charge_added",
        sortField: "charge_applied_date",
        title: "Charge Added",
        dataClass: "center aligned",
        titleClass: "center aligned",
        width: "200px"
    },
    {
        name: "welcome_letter_sent_date",
        sortField: "welcome_letter_sent_date",
        title: "Welcome Letter Sent",
        dataClass: "center aligned",
        titleClass: "center aligned",
        callback: formatDate,
        width: "200px"
    }
]

export const tenantListColumns = [
    {
        name: "__slot:checkbox",
        dataClass: "center aligned no-click",
        width: "50px"
    },
    {
        name: "__slot:name",
        sortField: "last_name",
        title: "Name",
        width: "250px"
    },
    {
        name: "unit",
        sortField: "unit",
        title: "Unit",
        dataClass: "center aligned",
        titleClass: "center aligned",
        width: "125px"
    },
    {
        name: "facility_number",
        sortField: "facility_number",
        title: "Facility Number",
        dataClass: "center aligned",
        titleClass: "center aligned",
        width: "200px"
    },
    {
        name: "__slot:status",
        title: "Status",
        width: "250px"
    },
    {
        name: "billing_date",
        sortField: "billing_date",
        title: "Billing Date",
        dataClass: "center aligned",
        titleClass: "center aligned",
        width: "175px"
    },
    {
        name: "insurance_effective_date",
        sortField: "insurance_effective_date",
        title: "Insurance Effective Date",
        dataClass: "center aligned",
        titleClass: "center aligned",
        callback: formatDate,
        width: "250px"
    },
    {
        name: "__slot:setup_confirmed",
        sortField: "charge_applied_date",
        title: "Setup Confirmed",
        dataClass: "center aligned",
        titleClass: "center aligned",
        width: "175px"
    },
    {
        name: "__slot:api_confirmed",
        sortField: "charge_confirmed_date",
        title: "API Confirmed Setup",
        dataClass: "center aligned",
        titleClass: "center aligned",
        width: "175px"
    },
    {
        name: "__slot:payment_confirmed",
        sortField: "charge_confirmed_date",
        title: "Payment Confirmed",
        dataClass: "center aligned",
        titleClass: "center aligned",
        width: "175px"
    },
    {
        name: "initial_letter_sent_date",
        sortField: "initial_letter_sent_date",
        title: "Audit Letter Sent",
        dataClass: "center aligned",
        titleClass: "center aligned",
        callback: formatDate,
        width: "200px"
    },
    {
        name: "reminder_letter_sent_date",
        sortField: "reminder_letter_sent_date",
        title: "Reminder Letter Sent",
        dataClass: "center aligned",
        titleClass: "center aligned",
        callback: formatDate,
        width: "200px"
    },
    {
        name: "__slot:welcome_letter_sent_date",
        sortField: "welcome_letter_sent_date",
        title: "Welcome Letter Sent",
        dataClass: "center aligned",
        titleClass: "center aligned",
        width: "200px"
    }
]

export const archivedFacilitiesColumns = [
    {
        name: "facility_number",
        title: "Facility Number",
        sortField: "facility_number",
        width: "175px"
    },
    {
        name: "letter_effective_date",
        title: "Letter Effective Date",
        sortField: "letter_effective_date",
        width: "225px"
    },
    {
        name: "tenant_count",
        title: "Tenant Count",
        sortField: "tenant_count",
        titleClass: "center aligned",
        dataClass: "center aligned",
        width: "150px"
    },
    {
        name: "__slot:percent_complete",
        title: "Complete %",
        titleClass: "center aligned",
        dataClass: "center aligned",
        sortField: "percent_complete",
        width: "150px"
    },
    {
        name: "__slot:download",
        title: "Download",
        titleClass: "right aligned",
        dataClass: "right aligned"
    }
]

export const facilityStatsColumns = [
    {
        name: "__slot:facility_number",
        sortField: "facility_number",
        title: "Facility Number",
        width: "150px"
    },
    {
        name: "prime_site_id",
        sortField: "prime_site_id",
        title: "Prime ID",
        width: "150px"
    },
    {
        name: "__slot:account_name",
        sortField: "account_name",
        title: "Facility Name",
        width: "300px"
    },
    {
        name: "stats.tenant_count",
        title: "Total In Program",
        width: "150px",
        titleClass: "center aligned",
        dataClass: "center aligned"
    },
    {
        name: "__slot:percent_complete",
        title: "Complete %",
        width: "125px",
        titleClass: "center aligned",
        dataClass: "center aligned"
    },
    {
        name: "__slot:archive",
        title: "Wrap-Up",
        width: "125px",
        titleClass: "center aligned",
        dataClass: "center aligned no-click"
    },
    {
        name: "__slot:selected_coverage_percentage",
        title: "Selected Coverage %",
        width: "175px",
        titleClass: "right aligned",
        dataClass: "right aligned"
    },
    {
        name: "__slot:confirmed_payment_percentage",
        title: "Coverage Confirmed %",
        width: "185px",
        titleClass: "right aligned",
        dataClass: "right aligned"
    },
    {
        name: "__slot:provided_proof_percentage",
        title: "Provided Proof %",
        width: "150px",
        titleClass: "right aligned",
        dataClass: "right aligned"
    },
    {
        name: "__slot:opted_out_percentage",
        title: "Opted Out %",
        width: "115px",
        titleClass: "right aligned",
        dataClass: "right aligned"
    },
    {
        name: "__slot:moved_out_percentage",
        title: "Moved Out %",
        width: "125px",
        titleClass: "right aligned",
        dataClass: "right aligned"
    },
    {
        name: "__slot:bad_address_percentage",
        title: "Bad Address %",
        width: "145px",
        titleClass: "right aligned",
        dataClass: "right aligned"
    }
]



// WEBPACK FOOTER //
// ./src/utilities/columns.js